import { useState, useEffect, useRef } from "react";
import { ChevronDown, Menu, X, LogIn, LogOut, Home, Building2, ShieldCheck, User, ShoppingBag, Store, Wrench } from "lucide-react";
import DarkModeToggle from "./DarkModeToggle";
import { useAuth } from "@/_core/hooks/useAuth";
import { getLoginUrl } from "@/const";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";

const LOGO_URL = "https://files.manuscdn.com/user_upload_by_module/session_file/310519663035723669/aUVBGhJWWIApMxbQ.png";

const navLinks = [
  { href: "#about", label: "About" },
  { href: "#community", label: "Community" },
  { href: "#committee", label: "Leadership" },
  { href: "#advertise", label: "Advertise" },
  { href: "#apartment-marketplace", label: "Apartment Marketplace" },
  { href: "#contact", label: "Contact" },
];

const pagesLinks = [
  { href: "/services", label: "Services" },
  { href: "/amenities", label: "Amenities & Facilities" },
  { href: "/investment-location", label: "Investment & Location" },
  { href: "/bylaws", label: "Read By-Laws" },
];

/** Returns initials from a display name, e.g. "Pradeep Kumar" → "PK" */
function getInitials(name: string | null | undefined): string {
  if (!name) return "?";
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}

const Header = () => {
  const [showPagesMenu, setShowPagesMenu] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobilePagesOpen, setMobilePagesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const { user, isAuthenticated, loading } = useAuth();
  const logoutMutation = trpc.auth.logout.useMutation({
    onSuccess: () => {
      toast.success("Signed out successfully");
      window.location.href = "/";
    },
    onError: () => {
      toast.error("Sign out failed. Please try again.");
    },
  });

  // Check community orders access (only fires when user is loaded)
  const ordersAccess = trpc.communityOrders.checkAccess.useQuery(undefined, {
    enabled: !loading,
    retry: false,
    staleTime: 60_000,
  });

  // Check maintenance access (only fires when user is loaded)
  const maintenanceAccess = trpc.maintenance.checkAccess.useQuery(undefined, {
    enabled: !loading,
    retry: false,
    staleTime: 60_000,
  });

  const showOrdersNav = ordersAccess.data?.hasAccess === true;
  const showMaintenanceNav = maintenanceAccess.data?.hasAccess === true;
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Hover handlers for desktop Pages dropdown with small delay to prevent flicker
  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setShowPagesMenu(true);
  };

  const handleMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => setShowPagesMenu(false), 150);
  };

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    };
  }, []);

  const handleLogin = () => {
    // Pass current page path so user lands back here after login
    window.location.href = getLoginUrl(window.location.pathname);
  };

  const handleLogout = () => {
    setShowUserMenu(false);
    logoutMutation.mutate();
  };

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 dark:bg-slate-900/95 backdrop-blur-md shadow-lg border-b border-slate-100 dark:border-slate-700"
          : "bg-white dark:bg-slate-900 shadow-sm border-b border-slate-200 dark:border-slate-700"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a
            href="/"
            className="flex items-center gap-3 hover:opacity-90 transition-opacity flex-shrink-0"
          >
            <img
              src={LOGO_URL}
              alt="Purva Swanlake"
              className="h-16 sm:h-20 w-auto object-contain"
            />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 rounded-lg hover:text-blue-700 dark:hover:text-blue-400 hover:bg-blue-50/80 dark:hover:bg-blue-900/30 transition-all duration-200"
              >
                {link.label}
              </a>
            ))}

            {/* Community Orders nav — only shown when user has access */}
            {showOrdersNav && (
              <a
                href="/order"
                className="px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 rounded-lg hover:text-green-700 dark:hover:text-green-400 hover:bg-green-50/80 dark:hover:bg-green-900/30 transition-all duration-200 flex items-center gap-1.5"
              >
                <ShoppingBag className="w-3.5 h-3.5" />
                Orders
              </a>
            )}

            {/* Maintenance nav — only shown when user has access */}
            {showMaintenanceNav && (
              <a
                href="/maintenance"
                className="px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 rounded-lg hover:text-amber-700 dark:hover:text-amber-400 hover:bg-amber-50/80 dark:hover:bg-amber-900/30 transition-all duration-200 flex items-center gap-1.5"
              >
                <Wrench className="w-3.5 h-3.5" />
                Maintenance
              </a>
            )}

            {/* Pages Dropdown - Hover on desktop */}
            <div
              className="relative pages-dropdown"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <button
                className={`flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                  showPagesMenu
                    ? "text-blue-700 dark:text-blue-400 bg-blue-50/80 dark:bg-blue-900/30"
                    : "text-slate-700 dark:text-slate-200 hover:text-blue-700 dark:hover:text-blue-400 hover:bg-blue-50/80 dark:hover:bg-blue-900/30"
                }`}
              >
                Pages
                <ChevronDown
                  className={`w-3.5 h-3.5 transition-transform duration-200 ${
                    showPagesMenu ? "rotate-180" : ""
                  }`}
                />
              </button>

              {/* Dropdown Menu */}
              <div
                className={`absolute right-0 mt-1 w-52 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-xl shadow-xl transition-all duration-200 overflow-hidden ${
                  showPagesMenu
                    ? "opacity-100 visible translate-y-0"
                    : "opacity-0 invisible -translate-y-1"
                }`}
              >
                {pagesLinks.map((link, i) => (
                  <a
                    key={link.href}
                    href={link.href}
                    className={`block px-4 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:text-blue-700 dark:hover:text-blue-400 transition-colors duration-150 ${
                      i < pagesLinks.length - 1 ? "border-b border-slate-100 dark:border-slate-700" : ""
                    }`}
                    onClick={() => setShowPagesMenu(false)}
                  >
                    {link.label}
                  </a>
                ))}
              </div>
            </div>

            {/* Dark Mode Toggle */}
            <div className="ml-2 pl-2 border-l border-slate-200 dark:border-slate-600">
              <DarkModeToggle />
            </div>

            {/* Login / User Avatar */}
            <div className="ml-2 pl-2 border-l border-slate-200 dark:border-slate-600" ref={userMenuRef}>
              {loading ? (
                <div className="w-9 h-9 rounded-full bg-slate-200 dark:bg-slate-700 animate-pulse" />
              ) : isAuthenticated && user ? (
                <div className="relative">
                  <button
                    onClick={() => setShowUserMenu((v) => !v)}
                    className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-blue-50/80 dark:hover:bg-blue-900/30 transition-all duration-200 group"
                    aria-label="User menu"
                  >
                    {/* Avatar circle with initials */}
                    <div className="w-8 h-8 rounded-full bg-blue-600 dark:bg-blue-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0 ring-2 ring-blue-100 dark:ring-blue-800">
                      {getInitials(user.name)}
                    </div>
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-200 max-w-[100px] truncate">
                      {user.name ?? user.email ?? "Resident"}
                    </span>
                    <ChevronDown
                      className={`w-3.5 h-3.5 text-slate-500 transition-transform duration-200 ${
                        showUserMenu ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {/* User dropdown */}
                  <div
                    className={`absolute right-0 mt-1 w-56 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-xl shadow-xl transition-all duration-200 overflow-hidden ${
                      showUserMenu
                        ? "opacity-100 visible translate-y-0"
                        : "opacity-0 invisible -translate-y-1"
                    }`}
                  >
                    {/* User info header */}
                    <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700">
                      <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">
                        {user.name ?? "Resident"}
                      </p>
                      {user.email && (
                        <p className="text-xs text-slate-500 dark:text-slate-400 truncate mt-0.5">
                          {user.email}
                        </p>
                      )}
                    </div>

                    {/* Menu items */}
                    <a
                      href="/apartments"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:text-blue-700 dark:hover:text-blue-400 transition-colors duration-150"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <Home className="w-4 h-4 flex-shrink-0" />
                      Browse Listings
                    </a>
                    <a
                      href="/my-apartments"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:text-blue-700 dark:hover:text-blue-400 transition-colors duration-150"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <Building2 className="w-4 h-4 flex-shrink-0" />
                      My Listings
                    </a>

                    {/* Community Orders link — only when user has access */}
                    {showOrdersNav && (
                      <a
                        href="/order"
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-green-50 dark:hover:bg-green-900/30 hover:text-green-700 dark:hover:text-green-400 transition-colors duration-150"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <ShoppingBag className="w-4 h-4 flex-shrink-0 text-green-600" />
                        Community Orders
                      </a>
                    )}

                    {/* Maintenance link — only when user has access */}
                    {showMaintenanceNav && (
                      <a
                        href="/maintenance"
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-amber-50 dark:hover:bg-amber-900/30 hover:text-amber-700 dark:hover:text-amber-400 transition-colors duration-150"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <Wrench className="w-4 h-4 flex-shrink-0 text-amber-600" />
                        Maintenance
                      </a>
                    )}

                    {/* Admin links — only shown for admin users */}
                    {isAdmin && (
                      <>
                        <div className="border-t border-slate-100 dark:border-slate-700" />
                        <a
                          href="/admin/apartments-moderation"
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:text-blue-700 dark:hover:text-blue-400 transition-colors duration-150"
                          onClick={() => setShowUserMenu(false)}
                        >
                          <ShieldCheck className="w-4 h-4 flex-shrink-0 text-amber-500" />
                          Admin: Moderation
                        </a>
                        <a
                          href="/admin/orders"
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:text-blue-700 dark:hover:text-blue-400 transition-colors duration-150"
                          onClick={() => setShowUserMenu(false)}
                        >
                          <Store className="w-4 h-4 flex-shrink-0 text-amber-500" />
                          Admin: Orders
                        </a>
                        <a
                          href="/admin/maintenance"
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:text-blue-700 dark:hover:text-blue-400 transition-colors duration-150"
                          onClick={() => setShowUserMenu(false)}
                        >
                          <Wrench className="w-4 h-4 flex-shrink-0 text-amber-500" />
                          Admin: Maintenance
                        </a>
                      </>
                    )}

                    {/* Logout */}
                    <button
                      onClick={handleLogout}
                      disabled={logoutMutation.isPending}
                      className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors duration-150 border-t border-slate-100 dark:border-slate-700 disabled:opacity-50"
                    >
                      <LogOut className="w-4 h-4 flex-shrink-0" />
                      {logoutMutation.isPending ? "Signing out…" : "Sign Out"}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={handleLogin}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 rounded-lg transition-all duration-200 shadow-sm"
                >
                  <LogIn className="w-4 h-4" />
                  Sign In
                </button>
              )}
            </div>
          </nav>

          {/* Mobile: Dark Mode + Hamburger */}
          <div className="flex items-center gap-2 lg:hidden">
            <DarkModeToggle />
            {/* Mobile login/avatar button */}
            {!loading && (
              isAuthenticated && user ? (
                <button
                  onClick={() => setShowUserMenu((v) => !v)}
                  className="w-8 h-8 rounded-full bg-blue-600 dark:bg-blue-500 flex items-center justify-center text-white text-xs font-bold ring-2 ring-blue-100 dark:ring-blue-800"
                  aria-label="User menu"
                >
                  {getInitials(user.name)}
                </button>
              ) : (
                <button
                  onClick={handleLogin}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                >
                  <LogIn className="w-3.5 h-3.5" />
                  Sign In
                </button>
              )
            )}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <X className="w-5 h-5" />
              ) : (
                <Menu className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile User Menu (shown when avatar clicked on mobile) */}
      {isAuthenticated && user && showUserMenu && (
        <div className="lg:hidden border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-3 space-y-1">
          <div className="flex items-center gap-3 px-3 py-2 mb-2">
            <div className="w-9 h-9 rounded-full bg-blue-600 dark:bg-blue-500 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
              {getInitials(user.name)}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">{user.name ?? "Resident"}</p>
              {user.email && <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{user.email}</p>}
            </div>
          </div>
          <a href="/apartments" className="flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg" onClick={() => { setShowUserMenu(false); setMobileMenuOpen(false); }}>
            <Home className="w-4 h-4" /> Browse Listings
          </a>
          <a href="/my-apartments" className="flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg" onClick={() => { setShowUserMenu(false); setMobileMenuOpen(false); }}>
            <Building2 className="w-4 h-4" /> My Listings
          </a>
          {showOrdersNav && (
            <a href="/order" className="flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-green-50 dark:hover:bg-green-900/30 rounded-lg" onClick={() => { setShowUserMenu(false); setMobileMenuOpen(false); }}>
              <ShoppingBag className="w-4 h-4 text-green-600" /> Community Orders
            </a>
          )}
          {showMaintenanceNav && (
            <a href="/maintenance" className="flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-amber-50 dark:hover:bg-amber-900/30 rounded-lg" onClick={() => { setShowUserMenu(false); setMobileMenuOpen(false); }}>
              <Wrench className="w-4 h-4 text-amber-600" /> Maintenance
            </a>
          )}
          {isAdmin && (
            <>
              <a href="/admin/apartments-moderation" className="flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg" onClick={() => { setShowUserMenu(false); setMobileMenuOpen(false); }}>
                <ShieldCheck className="w-4 h-4 text-amber-500" /> Admin: Moderation
              </a>
              <a href="/admin/orders" className="flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg" onClick={() => { setShowUserMenu(false); setMobileMenuOpen(false); }}>
                <Store className="w-4 h-4 text-amber-500" /> Admin: Orders
              </a>
              <a href="/admin/maintenance" className="flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg" onClick={() => { setShowUserMenu(false); setMobileMenuOpen(false); }}>
                <Wrench className="w-4 h-4 text-amber-500" /> Admin: Maintenance
              </a>
            </>
          )}
          <button
            onClick={handleLogout}
            disabled={logoutMutation.isPending}
            className="flex items-center gap-3 w-full px-3 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg disabled:opacity-50"
          >
            <LogOut className="w-4 h-4" />
            {logoutMutation.isPending ? "Signing out…" : "Sign Out"}
          </button>
        </div>
      )}

      {/* Mobile Menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 ${
          mobileMenuOpen ? "max-h-[500px] border-t border-slate-200 dark:border-slate-700" : "max-h-0"
        }`}
      >
        <div className="px-4 py-3 bg-white dark:bg-slate-900 space-y-1">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="block px-3 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-200 hover:text-blue-700 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              {link.label}
            </a>
          ))}

          {/* Community Orders link in mobile nav — only when user has access */}
          {showOrdersNav && (
            <a
              href="/order"
              className="flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-200 hover:text-green-700 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30 rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              <ShoppingBag className="w-4 h-4 text-green-600" />
              Community Orders
            </a>
          )}

          {/* Maintenance link in mobile nav — only when user has access */}
          {showMaintenanceNav && (
            <a
              href="/maintenance"
              className="flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-200 hover:text-amber-700 dark:hover:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/30 rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              <Wrench className="w-4 h-4 text-amber-600" />
              Maintenance
            </a>
          )}

          {/* Mobile Pages Accordion */}
          <div className="pt-2 mt-2 border-t border-slate-100 dark:border-slate-700">
            <button
              onClick={() => setMobilePagesOpen(!mobilePagesOpen)}
              className="flex items-center justify-between w-full px-3 py-2.5 text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider hover:text-blue-700 dark:hover:text-blue-400 rounded-lg transition-colors"
            >
              <span>Pages</span>
              <ChevronDown
                className={`w-4 h-4 transition-transform duration-200 ${
                  mobilePagesOpen ? "rotate-180" : ""
                }`}
              />
            </button>
            <div
              className={`overflow-hidden transition-all duration-300 ${
                mobilePagesOpen ? "max-h-48 opacity-100" : "max-h-0 opacity-0"
              }`}
            >
              {pagesLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="block px-3 py-2.5 pl-6 text-sm font-medium text-slate-700 dark:text-slate-200 hover:text-blue-700 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
